import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { IconButton, Menu } from '@mui/material';
import React, { useState, FC, MouseEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import getChatConversation from '../../../api/getChatConversation';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, TypographyStyledGlobal, fontWeight, fontSize } from '../../../theme';
import { MenuItemsStyled } from '../../../theme/style';
import ExportFormat from '../../../utils/enum/ExportFormat';
import Loader from '../../Loader/loader';
import ChatbotModal from '../../Modals/DashboardModal/chatbotModal';
import TextTypingEffectWithTextsFadeOut from '../../Modals/DashboardModal/DashboardModalContent/ChatbotModal/typingEffect';
import { ChatingProps } from '../../Modals/DashboardModal/dashboardModalInterface';
import { ClauseFilterLabelProps, ScopeLabelProps } from '../dashboardInterfaces';
import { ButtonDiv, ConversationButtonStyled, TextVerbatimStyled, ChatbotContainerStyled } from '../styled';
import { ReactComponent as ChatbotConversationImage } from '../../../assets/charts/chatbot-conversation.svg';
import { ReactComponent as ChatbotSummaryImage } from '../../../assets/charts/chatbot-summary.svg';

type ChatbotIndicatorProps = {
    question: any,
    selectedClauseFilterLabel?: Array<ClauseFilterLabelProps>,
    getChatbotResponse: (isSummary: boolean, question: string) => void,
    conversation: Array<ChatingProps>,
    summary: string,
    isSummaryDisplayed: boolean,
    waitingForChatAnswer: boolean,
    handleIsRenderedChat: (chatIdRendered: number) => void,
    surveyId: string
    selectedScopeLabel?: Array<ScopeLabelProps>
    surveyName?: string
    dashboardName?: string
}

const ChatbotIndicator: FC<ChatbotIndicatorProps> = ({ question, selectedClauseFilterLabel, getChatbotResponse, conversation, summary, isSummaryDisplayed, waitingForChatAnswer, handleIsRenderedChat, surveyId, selectedScopeLabel, surveyName, dashboardName }) => {
    const [openModal, setOpenModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { t } = useTranslation('dashboard');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const filterClauseLabel = selectedClauseFilterLabel ? selectedClauseFilterLabel.map((f: ClauseFilterLabelProps) => `${f.filterLabel} : ${f.modalitiesLabel}`).join(' || ') : undefined;
    const scopeClauseLabel = selectedScopeLabel ? selectedScopeLabel.map((s: ScopeLabelProps) => `${s.scopeLabel} : ${s.modalitiesLabel}`).join(' || ') : undefined;

    const handleOpenDownloadMenu = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setAnchorEl(null);
    };

    const handleDownload = async (format: ExportFormat) => {
        handleCloseDownloadMenu();
        await getChatConversation(surveyId, question.label, scopeClauseLabel || t('filter.noFilter'), filterClauseLabel || t('filter.noFilter'), [], summary, format, surveyName, dashboardName, selectedSchema || '');
    };

    return (
        <>
            <ChatbotContainerStyled scroll='scroll'>
                <TypographyStyledGlobal fontSize='16px' margin='0px 0px 0px 15px' padding='10px 0px 0px 0px' textAlign='left' weight='500'>
                    {isSummaryDisplayed ? t('addIndicatorModal.addChatbotIndicator.title') : t('addIndicatorModal.addChatbotIndicator.chooseAnAction')}
                </TypographyStyledGlobal>
            </ChatbotContainerStyled>
            { isSummaryDisplayed && (
                <ChatbotContainerStyled scroll={summary ? 'scroll' : 'none'}>
                    { summary && (
                        <TextVerbatimStyled>
                            <TextTypingEffectWithTextsFadeOut handleIsRenderedChat={handleIsRenderedChat} text={summary} hasToUseTypingEffect />
                        </TextVerbatimStyled>
                    )}
                    { !summary && (
                        <Loader size={30} />
                    )}
                </ChatbotContainerStyled>
            )}
            <ButtonDiv>
                <ConversationButtonStyled bgColor={colors.default} bgColorHover={colors.defaultHover} fontColor='white' margin='0px 0px 0px 15px' onClick={() => { getChatbotResponse(true, ''); }} boxShadow>
                    <ChatbotSummaryImage height={25} width={25} />
                    <TypographyStyledGlobal
                        margin='0px'
                        style={{ paddingRight: 15, borderRight: '1px solid white' }}
                        weight={fontWeight.regular}
                    >
                        {t('addIndicatorModal.addChatbotIndicator.summary')}
                    </TypographyStyledGlobal>
                    <IconButton
                        disabled={!summary}
                        onClick={handleOpenDownloadMenu}
                        size='small'
                        sx={{ color: 'white', padding: '2px', '&:disabled': { color: 'rgba(255,255,255,0.4)' } }}
                    >
                        <DownloadOutlinedIcon fontSize='small' />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        onClick={(e) => e.stopPropagation()}
                        onClose={handleCloseDownloadMenu}
                        open={Boolean(anchorEl)}
                    >
                        <MenuItemsStyled onClick={() => handleDownload(ExportFormat.CSV)} disableRipple>
                            <TypographyStyledGlobal textAlign='center' weight={fontWeight.semiRegular} width='100%'>
                                {t('exportDashboardCsv')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                        <MenuItemsStyled onClick={() => handleDownload(ExportFormat.TXT)} disableRipple>
                            <TypographyStyledGlobal textAlign='center' weight={fontWeight.semiRegular} width='100%'>
                                {t('exportDashboardTxt')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                        <MenuItemsStyled onClick={() => handleDownload(ExportFormat.WORD)} disableRipple>
                            <TypographyStyledGlobal textAlign='center' weight={fontWeight.semiRegular} width='100%'>
                                {t('exportDashboardWord')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                    </Menu>
                </ConversationButtonStyled>
                <ConversationButtonStyled bgColor={colors.default} bgColorHover={colors.defaultHover} fontColor='white' margin='0px 0px 0px 15px' onClick={() => setOpenModal(true)} boxShadow>
                    <ChatbotConversationImage height={25} width={25} />
                    <TypographyStyledGlobal
                        margin='0px'
                        weight={fontWeight.regular}
                    >
                        {t('addIndicatorModal.addChatbotIndicator.conversation')}
                    </TypographyStyledGlobal>
                </ConversationButtonStyled>
            </ButtonDiv>
            <ChatbotContainerStyled scroll='none'>
                <TypographyStyledGlobal
                    color={colors.grey400}
                    margin='15px 0px 0px 15px'
                    size={fontSize.m}
                    textAlign='left'
                    weight={fontWeight.semiRegular}
                >
                    {t('addIndicatorModal.addChatbotIndicator.aiGeneratedContent')}
                </TypographyStyledGlobal>
            </ChatbotContainerStyled>
            <ChatbotModal conversation={conversation} dashboardName={dashboardName} getChatbotResponse={getChatbotResponse} handleIsRenderedChat={handleIsRenderedChat} openModal={openModal} questionLabel={question.label} selectedClauseFilterLabel={selectedClauseFilterLabel} selectedScopeLabel={selectedScopeLabel} setOpenModal={setOpenModal} summary={summary} surveyId={surveyId} surveyName={surveyName} waitingForChatAnswer={waitingForChatAnswer} />
        </>
    );
};

export default ChatbotIndicator;
